/**
 * [화면 편집] 의 계산 — 요소를 옮기고 크기를 바꿀 때 무엇을 어떻게 적나(2026-10-30).
 *
 * 편집기는 HTML 을 스크립트 없이 띄우고(`sandbox="allow-same-origin"`), 사람이 화면에서 고른 요소에 **그 요소의 인라인
 * 스타일만** 더한다. 문서의 짜임(태그·클래스·스크립트)은 건드리지 않는다 — 비전문가가 고친 문서도 만든 사람이 다시 열어
 * 알아볼 수 있어야 하고, 스크립트로 값을 그리는 현황판은 화면에서 그대로 돌아야 한다.
 *
 * 옮기기는 `left`·`top` 으로 한다. `transform: translate` 나 `translate` 속성이 더 깔끔하지만, 앞의 것은 요소가 이미 가진 변환
 * (가운데 맞춤·회전)을 덮어쓰고 뒤의 것은 안드로이드 5.1 웹뷰(Chromium 39)가 모른다. 흐름 속 요소(static)는 `relative` 로
 * 바꿔 옮긴다 — 제자리는 비워 둔 채 그림만 옮겨 가므로 다른 요소가 따라 밀리지 않는다.
 *
 * DOM 없이 시험할 수 있게 계산만 여기 둔다. DOM 을 만지는 일은 `HtmlVisualEditor.tsx` 가 한다.
 */

export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** 크기 손잡이 — 네 변과 네 모서리. */
export type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';
export const HANDLES: readonly Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];

/** 이보다 작게는 줄이지 않는다 — 손잡이를 다시 잡을 수 없게 된다. */
export const MIN_SIZE = 8;

const round = (value: number): number => Math.round(value);

/**
 * 손잡이를 `dx`·`dy` 만큼 끌었을 때의 새 상자. 왼쪽·위 손잡이는 반대쪽 변을 붙잡아 두고 늘린다.
 * `keepRatio` 면 모서리 손잡이가 처음 비율을 지킨다 — 그림을 끌어 키울 때 찌그러지지 않게(그림의 기본값).
 */
export function resizeBox(start: Box, handle: Handle, dx: number, dy: number, keepRatio = false): Box {
  let width = start.width;
  let height = start.height;
  if (handle.includes('e')) width = start.width + dx;
  if (handle.includes('w')) width = start.width - dx;
  if (handle.includes('s')) height = start.height + dy;
  if (handle.includes('n')) height = start.height - dy;
  width = Math.max(MIN_SIZE, width);
  height = Math.max(MIN_SIZE, height);
  if (keepRatio && handle.length === 2 && start.width > 0 && start.height > 0) {
    const ratio = start.width / start.height;
    // 더 크게 바뀐 쪽을 따른다 — 사람이 끈 방향이 그쪽이다(줄일 때도).
    if (Math.abs(width / start.width - 1) >= Math.abs(height / start.height - 1))
      height = Math.max(MIN_SIZE, width / ratio);
    else width = Math.max(MIN_SIZE, height * ratio);
  }
  const x = handle.includes('w') ? start.x + start.width - width : start.x;
  const y = handle.includes('n') ? start.y + start.height - height : start.y;
  return { x: round(x), y: round(y), width: round(width), height: round(height) };
}

/** 계산된 스타일 가운데 옮기기가 보는 것(`getComputedStyle`). */
export interface PositionStyle {
  position: string;
  left: string;
  top: string;
}

/** 옮기기를 시작한 자리 — 이 값에 끈 만큼을 더해 `left`·`top` 에 적는다. */
export interface MoveOrigin {
  position: 'relative' | 'absolute' | 'fixed';
  left: number;
  top: number;
}

const px = (value: string): number => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * 옮기기의 출발점. 흐름 속(static)·붙박이(sticky) 요소는 `relative` 로 바꿔 0 에서 시작한다 — `auto` 는 0 으로 본다.
 * 이미 자리를 가진 요소(relative·absolute·fixed)는 지금의 `left`·`top` 에서 시작한다.
 */
export function moveOrigin(style: PositionStyle): MoveOrigin {
  if (style.position === 'absolute' || style.position === 'fixed') {
    return { position: style.position, left: px(style.left), top: px(style.top) };
  }
  if (style.position === 'relative') return { position: 'relative', left: px(style.left), top: px(style.top) };
  return { position: 'relative', left: 0, top: 0 };
}

/** 출발점에서 `dx`·`dy` 만큼 옮긴 인라인 스타일. */
export function movedStyle(
  origin: MoveOrigin,
  dx: number,
  dy: number,
): { position: string; left: string; top: string } {
  return { position: origin.position, left: `${round(origin.left + dx)}px`, top: `${round(origin.top + dy)}px` };
}

/**
 * 상자 크기를 `width`·`height` 로 — `extra` 는 화면에 보이는 상자와 CSS 크기의 차이(안쪽 여백·테두리, `box-sizing:
 * content-box` 일 때). 그만큼 빼야 보이는 상자가 사람이 끈 크기가 된다.
 */
export function sizedStyle(
  size: { width: number; height: number },
  extra: { width: number; height: number },
): { width: string; height: string } {
  return {
    width: `${Math.max(0, round(size.width - extra.width))}px`,
    height: `${Math.max(0, round(size.height - extra.height))}px`,
  };
}

const MEDIA = new Set(['img', 'svg', 'picture', 'canvas']);
const TEXT = new Set([
  'p',
  'span',
  'strong',
  'em',
  'b',
  'i',
  'u',
  'small',
  'label',
  'a',
  'li',
  'td',
  'th',
  'font',
  'dt',
  'dd',
  'blockquote',
]);

/** 고른 요소를 사람이 부르는 이름으로 — 오른쪽 칸의 머리와 마우스를 올린 요소의 표시. */
export function elementLabel(tag: string, hasText: boolean, childElements: number): string {
  const name = tag.toLowerCase();
  if (MEDIA.has(name)) return '그림';
  if (name === 'video') return '영상';
  if (/^h[1-6]$/.test(name)) return '제목';
  if (name === 'table' || name === 'thead' || name === 'tbody' || name === 'tr') return '표';
  if (name === 'ul' || name === 'ol') return '목록';
  if (name === 'button') return '단추';
  if (name === 'input' || name === 'select' || name === 'textarea') return '입력 칸';
  if (name === 'iframe') return '끼운 화면';
  if (TEXT.has(name)) return '글';
  return hasText && childElements === 0 ? '글상자' : '상자';
}

/** 글자를 고칠 수 있는 요소인가 — 그림·입력 칸·표의 뼈대는 아니다. */
export function textEditable(tag: string): boolean {
  const name = tag.toLowerCase();
  return (
    !MEDIA.has(name) &&
    ![
      'video',
      'iframe',
      'input',
      'select',
      'textarea',
      'table',
      'thead',
      'tbody',
      'tr',
      'ul',
      'ol',
      'br',
      'hr',
    ].includes(name)
  );
}

/** `rgb(…)`·`rgba(…)` 를 색 고르기 칸이 아는 `#rrggbb` 로. 투명하거나 읽을 수 없으면 null. */
export function cssColorToHex(value: string): string | null {
  const match = /^rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+%?))?\s*\)$/i.exec(value.trim());
  if (!match) return /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim().toLowerCase() : null;
  const alpha =
    match[4] === undefined
      ? 1
      : match[4].endsWith('%')
        ? Number.parseFloat(match[4]) / 100
        : Number.parseFloat(match[4]);
  if (alpha === 0) return null;
  return `#${[match[1], match[2], match[3]].map((part) => Number(part).toString(16).padStart(2, '0')).join('')}`;
}

/** 편집 문서를 저장할 글자로 — 편집 중에 붙인 표시(글자 고치기)는 떼어 낸다. 문서 유형 선언은 원래 있던 것만 되살린다. */
export function serializeHtml(
  doctype: { name: string; publicId: string; systemId: string } | null,
  html: string,
): string {
  const head = doctype
    ? `<!DOCTYPE ${doctype.name}${doctype.publicId ? ` PUBLIC "${doctype.publicId}"` : ''}${doctype.systemId ? ` "${doctype.systemId}"` : ''}>\n`
    : '';
  return head + html;
}

/** 인라인 스타일을 선언마다 나눈다 — 따옴표·괄호 속의 `;`(예: `url("data:image/png;base64,…")`)는 나누지 않는다. */
function splitDeclarations(style: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let quote: string | null = null;
  let current = '';
  for (let index = 0; index < style.length; index += 1) {
    const char = style[index]!;
    current += char;
    if (quote) {
      if (char === '\\') current += style[++index] ?? '';
      else if (char === quote) quote = null;
      continue;
    }
    if (char === '"' || char === "'") quote = char;
    else if (char === '(') depth += 1;
    else if (char === ')') depth = Math.max(0, depth - 1);
    else if (char === ';' && depth === 0) {
      const text = current.slice(0, -1).trim();
      if (text) out.push(text);
      current = '';
    }
  }
  if (current.trim()) out.push(current.trim());
  return out;
}

/**
 * 인라인 스타일 글자에서 고칠 속성만 바꾼다(`null` 이면 지운다) — 나머지 선언은 글자 그대로 둔다.
 *
 * `element.style.left = …` 처럼 CSSOM 으로 쓰면 브라우저가 줄 전체를 자기 식으로 다시 적는다: 템플릿이 옛 웹뷰를 위해 적은
 * `-webkit-animation-duration` 같은 접두 속성을 떨구고, 네 변을 `inset:` 으로 합친다. 둘 다 안드로이드 5.1 웹뷰(Chromium 39)에서
 * 화면이 달라진다. 속성 이름은 CSS 이름(`font-size`)으로 준다. 있던 속성은 그 자리에서 바꾸고, 없던 것은 끝에 붙인다.
 */
export function patchStyle(style: string | null, changes: Readonly<Record<string, string | null>>): string {
  let declarations = splitDeclarations(style ?? '').map((text) => {
    const colon = text.indexOf(':');
    return { name: colon < 0 ? '' : text.slice(0, colon).trim().toLowerCase(), text };
  });
  for (const [name, value] of Object.entries(changes)) {
    const at = declarations.findIndex((declaration) => declaration.name === name);
    declarations = declarations.filter((declaration, index) => declaration.name !== name || index === at);
    if (value === null) {
      declarations = declarations.filter((declaration) => declaration.name !== name);
      continue;
    }
    const next = { name, text: `${name}: ${value}` };
    if (at < 0) declarations.push(next);
    else declarations[declarations.findIndex((declaration) => declaration.name === name)] = next;
  }
  return declarations.map((declaration) => `${declaration.text};`).join(' ');
}

/** 괄호 밖의 빈칸으로 나눈다 — `calc(100% - 8px)` 는 한 값이다. */
function splitTopLevel(value: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let current = '';
  for (const char of value.trim()) {
    if (char === '(') depth += 1;
    if (char === ')') depth = Math.max(0, depth - 1);
    if (/\s/.test(char) && depth === 0) {
      if (current) parts.push(current);
      current = '';
    } else current += char;
  }
  if (current) parts.push(current);
  return parts;
}

/**
 * 인라인 스타일의 `inset:` 을 네 변(`top`·`right`·`bottom`·`left`)으로 푼다. 브라우저는 네 변이 다 인라인에 있으면 한 줄로
 * 합쳐 적는데, 안드로이드 5.1 웹뷰(Chromium 39 — `inset` 은 Chromium 87 부터)는 그 줄을 몰라 자리를 잃는다. 저장하기 전에 푼다.
 */
export function expandInsetShorthand(style: string): string {
  return style.replace(
    /(^|;)(\s*)inset\s*:\s*([^;]+?)(\s*!important)?\s*(?=;|$)/gi,
    (_match, lead: string, space: string, value: string, important = '') => {
      const parts = splitTopLevel(value);
      if (parts.length < 1 || parts.length > 4) return `${lead}${space}inset: ${value}${important}`;
      const [top, right = top, bottom = top, left = right] = parts as [string, ...string[]];
      const flag = important ? ' !important' : '';
      return `${lead}${space}top: ${top}${flag}; right: ${right}${flag}; bottom: ${bottom}${flag}; left: ${left}${flag}`;
    },
  );
}

/** 손잡이마다 마우스 모양. */
export const HANDLE_CURSOR: Readonly<Record<Handle, string>> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

/** 손잡이의 자리(상자 안의 비율). */
export function handlePoint(box: Box, handle: Handle): { x: number; y: number } {
  const x = handle.includes('w') ? box.x : handle.includes('e') ? box.x + box.width : box.x + box.width / 2;
  const y = handle.includes('n') ? box.y : handle.includes('s') ? box.y + box.height : box.y + box.height / 2;
  return { x, y };
}

/** 편집 기록 — 되돌리기·다시 하기. 상한을 넘으면 가장 오래된 것부터 버린다. */
export class EditHistory {
  private entries: string[];
  private index: number;

  constructor(
    initial: string,
    private readonly limit = 60,
  ) {
    this.entries = [initial];
    this.index = 0;
  }

  get current(): string {
    return this.entries[this.index]!;
  }

  get canUndo(): boolean {
    return this.index > 0;
  }

  get canRedo(): boolean {
    return this.index < this.entries.length - 1;
  }

  /** 처음과 달라졌나 — 닫을 때 물을지. */
  get dirty(): boolean {
    return this.entries[this.index] !== this.entries[0];
  }

  /** 새 상태를 남긴다. 같은 글자면 남기지 않는다. 되돌린 뒤에 고치면 그 뒤의 다시 하기는 버린다. */
  push(state: string): boolean {
    if (state === this.current) return false;
    this.entries = this.entries.slice(0, this.index + 1);
    this.entries.push(state);
    if (this.entries.length > this.limit) this.entries.splice(1, this.entries.length - this.limit);
    this.index = this.entries.length - 1;
    return true;
  }

  undo(): string | null {
    if (!this.canUndo) return null;
    this.index -= 1;
    return this.current;
  }

  redo(): string | null {
    if (!this.canRedo) return null;
    this.index += 1;
    return this.current;
  }
}

// ── 맞춤선·정렬·색·확대(2026-10-30). 모두 DOM 없이 계산한다.

/** 맞춤선 — 끄는 상자의 변·가운데가 다른 상자의 변·가운데와 같은 줄에 놓였을 때 그리는 빨간 선. */
export interface Guide {
  axis: 'x' | 'y';
  /** 선의 자리 — x 축이면 세로선의 x. */
  at: number;
  from: number;
  to: number;
}

const linesX = (box: Box): number[] => [box.x, box.x + box.width / 2, box.x + box.width];
const linesY = (box: Box): number[] => [box.y, box.y + box.height / 2, box.y + box.height];

function nearest(moving: readonly number[], targets: readonly number[], threshold: number): number | null {
  let best: number | null = null;
  for (const from of moving) {
    for (const to of targets) {
      const delta = to - from;
      if (Math.abs(delta) <= threshold && (best === null || Math.abs(delta) < Math.abs(best))) best = delta;
    }
  }
  return best;
}

/** 지금 상자와 같은 줄에 놓인 둘레 상자의 변·가운데 — 선은 두 상자를 다 지나게 그린다. */
export function guidesFor(box: Box, targets: readonly Box[], tolerance = 0.5): Guide[] {
  const found = new Map<string, Guide>();
  const add = (axis: Guide['axis'], at: number, from: number, to: number): void => {
    const key = `${axis}:${Math.round(at * 2)}`;
    const seen = found.get(key);
    if (seen) {
      seen.from = Math.min(seen.from, from);
      seen.to = Math.max(seen.to, to);
    } else found.set(key, { axis, at, from, to });
  };
  for (const target of targets) {
    const top = Math.min(box.y, target.y);
    const bottom = Math.max(box.y + box.height, target.y + target.height);
    const left = Math.min(box.x, target.x);
    const right = Math.max(box.x + box.width, target.x + target.width);
    for (const mine of linesX(box))
      for (const at of linesX(target)) if (Math.abs(mine - at) <= tolerance) add('x', at, top, bottom);
    for (const mine of linesY(box))
      for (const at of linesY(target)) if (Math.abs(mine - at) <= tolerance) add('y', at, left, right);
  }
  return [...found.values()];
}

/** 끌어 옮기는 상자를 가까운 변·가운데에 붙인다 — 붙일 만큼(dx·dy)과 그릴 맞춤선. */
export function snapMove(
  box: Box,
  targets: readonly Box[],
  threshold: number,
): { dx: number; dy: number; guides: Guide[] } {
  const dx = nearest(linesX(box), targets.flatMap(linesX), threshold) ?? 0;
  const dy = nearest(linesY(box), targets.flatMap(linesY), threshold) ?? 0;
  return { dx, dy, guides: guidesFor({ ...box, x: box.x + dx, y: box.y + dy }, targets) };
}

/** 손잡이로 크기를 바꿀 때 — 움직이는 변만 붙인다. */
export function snapResize(
  box: Box,
  handle: Handle,
  targets: readonly Box[],
  threshold: number,
): { box: Box; guides: Guide[] } {
  let { x, y, width, height } = box;
  const xs = targets.flatMap(linesX);
  const ys = targets.flatMap(linesY);
  if (handle.includes('e')) {
    const delta = nearest([x + width], xs, threshold);
    if (delta !== null && width + delta >= MIN_SIZE) width += delta;
  }
  if (handle.includes('w')) {
    const delta = nearest([x], xs, threshold);
    if (delta !== null && width - delta >= MIN_SIZE) {
      x += delta;
      width -= delta;
    }
  }
  if (handle.includes('s')) {
    const delta = nearest([y + height], ys, threshold);
    if (delta !== null && height + delta >= MIN_SIZE) height += delta;
  }
  if (handle.includes('n')) {
    const delta = nearest([y], ys, threshold);
    if (delta !== null && height - delta >= MIN_SIZE) {
      y += delta;
      height -= delta;
    }
  }
  const next = { x, y, width, height };
  const edgesX = [...(handle.includes('w') ? [x] : []), ...(handle.includes('e') ? [x + width] : [])];
  const edgesY = [...(handle.includes('n') ? [y] : []), ...(handle.includes('s') ? [y + height] : [])];
  const guides = guidesFor(next, targets).filter((guide) =>
    (guide.axis === 'x' ? edgesX : edgesY).some((edge) => Math.abs(edge - guide.at) <= 0.5),
  );
  return { box: next, guides };
}

/** 여러 상자를 다 담는 상자. */
export function unionBox(boxes: readonly Box[]): Box | null {
  if (boxes.length === 0) return null;
  const left = Math.min(...boxes.map((box) => box.x));
  const top = Math.min(...boxes.map((box) => box.y));
  const right = Math.max(...boxes.map((box) => box.x + box.width));
  const bottom = Math.max(...boxes.map((box) => box.y + box.height));
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export type AlignMode = 'left' | 'center' | 'right' | 'top' | 'middle' | 'bottom';

/** 정렬 — 하나면 감싼 상자에, 여럿이면 고른 것들을 다 담는 상자에 맞춘다. 상자마다 옮길 만큼. */
export function alignDeltas(boxes: readonly Box[], mode: AlignMode, container: Box): Array<{ dx: number; dy: number }> {
  return boxes.map((box) => {
    let dx = 0;
    let dy = 0;
    if (mode === 'left') dx = container.x - box.x;
    if (mode === 'center') dx = container.x + container.width / 2 - (box.x + box.width / 2);
    if (mode === 'right') dx = container.x + container.width - (box.x + box.width);
    if (mode === 'top') dy = container.y - box.y;
    if (mode === 'middle') dy = container.y + container.height / 2 - (box.y + box.height / 2);
    if (mode === 'bottom') dy = container.y + container.height - (box.y + box.height);
    return { dx: round(dx), dy: round(dy) };
  });
}

// ── 색 — 계산된 색(`rgb()`·`rgba()`)을 읽고, 옛 웹뷰가 아는 꼴로 적는다.

export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

export function parseCssColor(value: string): Rgba | null {
  const text = value.trim().toLowerCase();
  if (text === 'transparent') return { r: 0, g: 0, b: 0, a: 0 };
  const functional = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:[,\s/]+([\d.]+%?))?\s*\)$/.exec(text);
  if (functional) {
    const alpha =
      functional[4] === undefined
        ? 1
        : functional[4].endsWith('%')
          ? Number.parseFloat(functional[4]) / 100
          : Number.parseFloat(functional[4]);
    return {
      r: Math.round(Number(functional[1])),
      g: Math.round(Number(functional[2])),
      b: Math.round(Number(functional[3])),
      a: Math.max(0, Math.min(1, alpha)),
    };
  }
  const hex = /^#([0-9a-f]{3,8})$/.exec(text)?.[1];
  if (!hex || ![3, 4, 6, 8].includes(hex.length)) return null;
  const full = hex.length <= 4 ? [...hex].map((char) => char + char).join('') : hex;
  const part = (index: number): number => Number.parseInt(full.slice(index, index + 2), 16);
  return { r: part(0), g: part(2), b: part(4), a: full.length === 8 ? Math.round((part(6) / 255) * 100) / 100 : 1 };
}

export function rgbHex(color: Pick<Rgba, 'r' | 'g' | 'b'>): string {
  return `#${[color.r, color.g, color.b].map((part) => Math.max(0, Math.min(255, part)).toString(16).padStart(2, '0')).join('')}`;
}

/** 적을 색 — 불투명하면 `#rrggbb`, 아니면 `rgba()`. `#rrggbbaa` 는 Chromium 62 부터라 안드로이드 5.1 웹뷰가 모른다. */
export function formatCssColor(hex: string, alpha: number): string {
  const color = parseCssColor(hex);
  if (!color) return hex;
  const a = Math.max(0, Math.min(1, alpha));
  if (a >= 0.995) return rgbHex(color);
  return `rgba(${color.r}, ${color.g}, ${color.b}, ${Math.round(a * 100) / 100})`;
}

// ── 확대·이동

export const ZOOM_MIN = 0.05;
export const ZOOM_MAX = 8;
const ZOOM_STEPS = [0.05, 0.1, 0.125, 0.25, 0.33, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8];

export interface Pan {
  x: number;
  y: number;
}

export function clampZoom(zoom: number): number {
  return Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, zoom));
}

/** 화면의 한 점(`at`, 캔버스 좌표)을 붙잡고 확대·축소 — 그 점 아래의 문서 자리가 그대로 남게 옮긴다. */
export function zoomAround(zoom: number, pan: Pan, at: Pan, next: number): Pan {
  const ratio = next / zoom;
  return { x: at.x - (at.x - pan.x) * ratio, y: at.y - (at.y - pan.y) * ratio };
}

/** 다음 확대 칸(+1)·이전 칸(-1). */
export function stepZoom(zoom: number, direction: 1 | -1): number {
  if (direction > 0) return ZOOM_STEPS.find((step) => step > zoom + 0.001) ?? ZOOM_MAX;
  return [...ZOOM_STEPS].reverse().find((step) => step < zoom - 0.001) ?? ZOOM_MIN;
}

/** 상자 하나(문서 전체·고른 것)를 캔버스 가운데에 다 보이게. */
export function fitView(
  viewport: { width: number; height: number },
  content: Box,
  padding = 56,
): { zoom: number; pan: Pan } {
  const zoom = clampZoom(
    Math.min(
      (viewport.width - padding * 2) / Math.max(1, content.width),
      (viewport.height - padding * 2) / Math.max(1, content.height),
    ),
  );
  return {
    zoom,
    pan: {
      x: Math.round((viewport.width - content.width * zoom) / 2 - content.x * zoom),
      y: Math.round((viewport.height - content.height * zoom) / 2 - content.y * zoom),
    },
  };
}

// ── 고르기

/**
 * 문서의 "틀" — 자식이 하나뿐이고 화면 대부분을 덮는 감싸개는 건너뛴다. 감싸개 하나에 다 든 HTML 이 흔한데, 그 감싸개를
 * 고르면 화면 전체가 골린다. 틀과 그 바깥은 고르지 않고(빈 곳을 누른 것으로 본다), 새로 넣는 글·그림·상자는 틀에 붙인다.
 */
export function descendFrame<T>(
  root: T,
  children: (node: T) => readonly T[],
  boxOf: (node: T) => Box,
  viewport: Box,
  cover = 0.8,
): T {
  let frame = root;
  for (let depth = 0; depth < 12; depth += 1) {
    const kids = children(frame);
    if (kids.length !== 1) break;
    const box = boxOf(kids[0]!);
    const width = Math.min(box.x + box.width, viewport.x + viewport.width) - Math.max(box.x, viewport.x);
    const height = Math.min(box.y + box.height, viewport.y + viewport.height) - Math.max(box.y, viewport.y);
    if (width <= 0 || height <= 0 || width * height < viewport.width * viewport.height * cover) break;
    frame = kids[0]!;
  }
  return frame;
}

/** 레이어 이름 — 직접 붙인 이름(`data-name`)이 먼저, 글이면 그 글, 상자면 종류와 첫 클래스. */
export function layerName(input: {
  tag: string;
  text: string;
  childElements: number;
  className: string;
  name?: string | null;
}): string {
  if (input.name?.trim()) return input.name.trim();
  const text = input.text.replace(/\s+/g, ' ').trim();
  const label = elementLabel(input.tag, !!text, input.childElements);
  if (input.childElements === 0 && text && textEditable(input.tag))
    return text.length > 28 ? `${text.slice(0, 27)}…` : text;
  const firstClass = input.className.trim().split(/\s+/)[0];
  return firstClass ? `${label} · ${firstClass}` : label;
}

/** 두 상자가 같은 자리·크기인가 — 감싼 상자를 고를 때 꼭 맞게 감싼 껍데기는 건너뛴다(골라도 달라 보이지 않는다). */
export function sameBox(a: Box, b: Box, tolerance = 1): boolean {
  return (
    Math.abs(a.x - b.x) <= tolerance &&
    Math.abs(a.y - b.y) <= tolerance &&
    Math.abs(a.width - b.width) <= tolerance &&
    Math.abs(a.height - b.height) <= tolerance
  );
}

/** 글자 크기 [−]·[+] 한 번 — 지금 크기의 10%(적어도 1px)씩. 큰 제목도 몇 번이면 눈에 띄게 바뀐다. */
export function stepFontSize(size: number, direction: 1 | -1): number {
  const step = Math.max(1, Math.round(size * 0.1));
  return Math.max(1, Math.min(999, Math.round(size) + direction * step));
}

/**
 * 이 화면이 쓰는 색 — 계산된 색 값(`rgb()` 등)을 많이 쓴 차례로. 검정·흰색과 반투명한 것은 뺀다(기본 칸에 이미 있다).
 * 색 고르기 맨 앞에 두어, 고친 글·상자가 화면의 원래 색과 어울리게 한다.
 */
export function topColors(values: Iterable<string>, limit = 6): string[] {
  const counts = new Map<string, number>();
  for (const value of values) {
    const color = parseCssColor(value);
    if (!color || color.a < 0.5) continue;
    const hex = rgbHex(color);
    if (hex === '#000000' || hex === '#ffffff') continue;
    counts.set(hex, (counts.get(hex) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([hex]) => hex);
}

/**
 * 그림을 넣기 전에 줄일 크기 — 긴 변이 `maxSide` 를 넘으면 비율대로 줄인다. 그림은 문서 안에 `data:` 로 함께 저장되므로
 * 사진 한 장(수 MB·4000px)을 그대로 넣으면 문서가 무거워지고 화면 기기가 느려진다. 화면보다 크게 둘 까닭이 없다.
 */
export function fitWithin(width: number, height: number, maxSide: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxSide || longest <= 0) return { width: Math.round(width), height: Math.round(height) };
  const scale = maxSide / longest;
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}
